<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('settings.confirmations.addNewConfirmation') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/potwierdzenia')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('settings.confirmations.addConfirmation') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5 p-4">
        <h4 class="mb-3 pb-3 border-bottom">
          {{ $t('ui.generalInfo') }}
        </h4>

        <validation-observer ref="confirmation-new-form" tag="form">
          <div class="form-group">
            <label for="name">
              {{ $t('settings.confirmations.confirmationName') }}:
            </label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="d-flex">
            <div class="w-50 mr-2">
              <div class="form-group">
                <label for="products">
                  {{ $t('settings.confirmations.products') }}:
                </label>

                <div class="d-flex">
                  <select-product
                    :returnProductNameAndCode="true"
                    :notRequired="true"
                    class="w-75"
                    ref="select-product" />

                  <d-button
                    :disabled="!selectedProduct.productID"
                    theme="success"
                    :outline="true"
                    @click.prevent="addProduct"
                    class="w-25 ml-2">
                    {{ $t('ui.add') }}
                  </d-button>

                  <d-button
                    theme="primary"
                    :outline="true"
                    @click.prevent="searchProducts"
                    class="w-25 ml-2">
                    {{ $t('ui.search') }}
                  </d-button>
                </div>

                <d-card
                  v-if="products.length"
                  class="mt-2 p-3">
                  <table class="table mb-0">
                    <tr
                      v-for="(productID, index) of products"
                      :key="'product-' + index">
                      <td class="w-100">
                        {{ productsData[productID] }}
                      </td>
                      <td>
                        <d-button
                          theme="danger"
                          @click.prevent="deleteProduct(productID)">
                          <i class="material-icons">delete</i>
                        </d-button>
                      </td>
                    </tr>
                  </table>
                </d-card>
              </div>
            </div>

            <div class="w-50 ml-2">
              <div class="form-group">
                <label for="customers">
                  {{ $t('settings.confirmations.customers') }}:
                </label>

                <div class="d-flex">
                  <select-customer
                    :returnCustomerName="true"
                    class="w-75"
                    ref="select-customer" />

                  <d-button
                    :disabled="!selectedCustomer.customerID"
                    theme="success"
                    :outline="true"
                    @click.prevent="addCustomer"
                    class="w-25 ml-2">
                    {{ $t('ui.add') }}
                  </d-button>
                </div>

                <d-card
                  v-if="customers.length"
                  class="mt-2 p-3">
                  <table class="table mb-0">
                    <tr
                      v-for="(customerID, index) of customers"
                      :key="'customer-' + index">
                      <td class="w-100">
                        {{ customersData[customerID] }}
                      </td>
                      <td>
                        <d-button
                          theme="danger"
                          @click.prevent="deleteCustomer(customerID)">
                          <i class="material-icons">delete</i>
                        </d-button>
                      </td>
                    </tr>
                  </table>
                </d-card>
              </div>
            </div>
          </div>
        </validation-observer>
      </d-card>
    </d-form>

    <confirmation-search-products-popup
      v-if="showProductsSearchPopup" />
  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from './../../utils/FormUtils.js';
import SelectCustomer from './../../components/common/SelectCustomer.vue';
import SelectProduct from './../../components/common/SelectProduct.vue';
import ConfirmationSearchProductsPopup from './ConfirmationSearchProductsPopup.vue';

export default {
  name: 'confirmation-new',
  components: {
    'confirmation-search-products-popup': ConfirmationSearchProductsPopup,
    'select-customer': SelectCustomer,
    'select-product': SelectProduct
  },
  data() {
    return {
      name: '',
      customers: [],
      products: [],
      // internals
      selectedCustomer: {
        customerID: null,
        customerName: ''
      },
      selectedProduct: {
        productID: null,
        productName: '',
        productCode: ''
      },
      customersData: {},
      productsData: {},
      showProductsSearchPopup: false
    };
  },
  mounted () {
    this.$bus.$on('ui-select-customer-updated', this.customerUpdated);
    this.$bus.$on('ui-select-product-updated', this.productUpdated);
    this.$bus.$on('confirmation-search-products-popup-close', this.searchProductsClosePopup);
    this.$bus.$on('confirmation-search-products-results', this.addProducts);
  },
  methods: {
    customerUpdated (newValue) {
      this.selectedCustomer = newValue;
    },
    productUpdated (newValue) {
      this.selectedProduct = newValue;
    },
    addCustomer () {
      let id = this.selectedCustomer.customerID;

      if (this.customers.indexOf(id) > -1) {
        Vue.swal({
          title: this.$t('settings.confirmations.selectedCustomerExists'),
          html: '',
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.customers.push(id);
      this.customersData[id] = this.selectedCustomer.customerName;
      this.$refs['select-customer'].resetValue();
    },
    addProduct () {
      let id = this.selectedProduct.productID;

      if (this.products.indexOf(id) > -1) {
        Vue.swal({
          title: this.$t('settings.confirmations.selectedProductExists'),
          html: '',
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.products.push(id);
      this.productsData[id] = this.selectedProduct.productCode + ' - ' + this.selectedProduct.productName;
      this.$refs['select-product'].resetValue();
    },
    deleteCustomer (customerID) {
      this.customers = this.customers.filter(customer => customer !== customerID);
      delete this.customersData[customerID];
    },
    deleteProduct (productID) {
      this.products = this.products.filter(product => product !== productID);
      delete this.productsData[productID];
    },
    searchProducts () {
      this.showProductsSearchPopup = true;
    },
    searchProductsClosePopup () {
      this.showProductsSearchPopup = false;
    },
    validateForm () {
      FormUtils.validate(this.$refs['confirmation-new-form'], this.submitForm);
    },
    submitForm () {
      if (!this.products.length && !this.customers.length) {
        Vue.swal({
          title: this.$t('settings.confirmations.productsOrCustomersRequired'),
          html: '',
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/confirmations/store',
        formData: {
          name: this.name,
          customers: this.customers,
          products: this.products
        },
        successTitle: this.$t('settings.confirmations.addConfirmationSuccessTitle'),
        successText: this.$t('settings.confirmations.addConfirmationSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.currency.addConfirmationErrorText'),
        successAction: () => {
          this.$router.push('/ustawienia/potwierdzenia')
        }
      });
    },
    addProducts (products) {
      for (let i = 0; i < products.length; i++) {
        let product = products[i];
        this.products.push(product.id);
        this.productsData[product.id] = product.code + ' - ' + product.name;
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated', this.customerUpdated);
    this.$bus.$off('ui-select-product-updated', this.productUpdated);
    this.$bus.$off('confirmation-search-products-popup-close', this.searchProductsClosePopup);
    this.$bus.$off('confirmation-search-products-results', this.addProducts);
  }
};
</script>
